/* eslint-disable no-case-declarations */
import React, { useEffect, useRef, useCallback } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { IFeatureCollectionModel, IFeaturesModel, IGeometryModel } from "@/util/http/Lightning/responseType"; // 引入类型定义
import { getColorForValue, ProbilityArray, ReversedProbabilityArray } from "../../lightningCommonFunc"; // 引入颜色获取函数
import { useTrackedEffect } from "ahooks";
import { LightningColorArray, LightningSearchTypeEnum } from "@/util/cosntants";

// 用于存储当前渲染的 Google Maps Polygon 实例，按 activeKey 分组
interface ResultPolygonMap {
    [activeKey: string]: google.maps.Polygon[];
}

interface SearchResultOverlayProps {
    activeKey: string; // 当前需要显示的 key (例如日期或 FirstStrike 的 title)
    finalSearchResult: Map<string, IFeaturesModel[]> | null;
    searchType?: string | null;
    // 新增回调函数，用于通知父组件所有多边形是否已预加载完成
    onAllPolygonsLoaded: (isLoaded: boolean) => void;
    currentKeyIndex?: number;
}


// 工具函数：计算 ring 的有向面积，用于判断方向
const ringArea = (ring: number[][]): number => {
    let sum = 0;
    for (let i = 0; i < ring.length - 1; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[i + 1];
        sum += (x2 - x1) * (y2 + y1);
    }
    return sum;
};

// 工具函数：保证外环为逆时针方向
/** 规范化环方向（并打印调试信息） */
export const normalizeRingDirection = (ring: number[][], debugLabel?: string): number[][] => {
    const area = ringArea(ring);
    const isClockwise = area > 0;

    // 如果需要统一成“外环逆时针”，就在这里做 reverse
    return isClockwise ? ring.slice().reverse() : ring;
};

const SearchResultOverlay: React.FC<SearchResultOverlayProps> = ({
    activeKey,
    searchType,
    finalSearchResult,
    onAllPolygonsLoaded, // 接收回调函数
    currentKeyIndex
}) => {
    const map = useMap();
    // 存储所有已创建的多边形实例，按 activeKey 分组
    const allRenderedPolygonsRef = useRef<ResultPolygonMap>({});
    // 存储当前显示在地图上的 activeKey
    const currentDisplayedKeyRef = useRef<string | null>(null);
    // 用于存储 InfoWindow 实例的 Ref
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);



    // 辅助函数：根据 searchType 或 feature.properties.value 动态获取颜色
    // 注意：这个函数现在接受一个可选的 featureValue 参数
    const getDynamicColors = useCallback((currentSearchType: string | null | undefined, featureValue?: string | null) => {
        let fillColor = "red"; // 默认颜色
        let strokeColor = "red"; // 默认颜色

        if (currentSearchType === LightningSearchTypeEnum.ALLCLEAR) {
            fillColor = LightningColorArray.Risk; // 深红
            strokeColor = LightningColorArray.Risk;
        } else if (currentSearchType === LightningSearchTypeEnum.DETECTED) {
            fillColor = LightningColorArray.DetectedStrike; // 蓝
            strokeColor = LightningColorArray.DetectedStrike;
        } else if (currentSearchType === LightningSearchTypeEnum.FIRSTSTRIKE || currentSearchType === LightningSearchTypeEnum.PROBABILITY) {
            // 当 searchType 为 'FirstStrike' 或 'Probability' 时，根据 featureValue 动态决定颜色
            if (featureValue !== undefined && featureValue !== null) {
                fillColor = getColorForValue(currentSearchType as LightningSearchTypeEnum.FIRSTSTRIKE | LightningSearchTypeEnum.PROBABILITY, featureValue);
                strokeColor = fillColor; // 边框颜色和填充颜色一致
            } else {
                // 如果没有 value，使用一个默认颜色，例如灰色
                fillColor = "gray";
                strokeColor = "gray";
            }
        }
        return { fillColor, strokeColor };
    }, []); // 依赖项现在只包含 getDynamicColors 自身，因为它内部不直接使用外部状态

    // --- 辅助函数：根据 GeoJSON 几何体获取 Google Maps Polygon 的路径 ---
    const getGooglePolygonPaths = useCallback((geometry: IGeometryModel): google.maps.LatLngLiteral[][] => {
        const pathsList: google.maps.LatLngLiteral[][] = [];
        if (!geometry.type || !geometry.coordinates) return pathsList;

        switch (geometry.type) {
            case "Polygon":
                const polygonCoords = geometry.coordinates as number[][][];
                if (polygonCoords && polygonCoords.length > 0) {
                    if(searchType === LightningSearchTypeEnum.FIRSTSTRIKE){
                        // when is FirstStrike, need to normalize the direction of rings
                        polygonCoords.forEach((ring, idx) => {
                            const normalizedRing = normalizeRingDirection(ring, `Polygon 环[${idx}]`);
                            pathsList.push(normalizedRing.map(([lng, lat]) => ({ lat, lng })));
                        });
                    }
                    else{
                        polygonCoords.forEach(ring => {
                            pathsList.push(ring.map(([lng, lat]) => ({ lat, lng })));
                        });
                    }

                }
                break;
            case "MultiPolygon":
                const multiPolygonCoords = (geometry.coordinates as number[][][][]) || [];
                for (const polygonPart of multiPolygonCoords) {
                    // 对于 MultiPolygon，每个 polygonPart 都是一个 GeoJSON Polygon 结构。
                    // Google Maps Polygon 支持 MultiPolygon 的每个部分作为独立的 Polygon。
                    if(searchType === LightningSearchTypeEnum.FIRSTSTRIKE){
                        polygonPart.forEach((ring, ringIdx) => {
                            // const normalizedRing = normalizeRingDirection(ring, `MultiPolygon[${idx}] 环[${ringIdx}]`);
                            const normalizedRing = normalizeRingDirection(ring, `MultiPolygon[${ringIdx}] 环[${ringIdx}]`);
                            pathsList.push(normalizedRing.map(([lng, lat]) => ({ lat, lng })));
                        });
                    }
                    else{
                        // 这里仍然只取外环，如果要处理 MultiPolygon 内部的挖空，需要将 polygonPart 的所有环传入
                        const outerRing = polygonPart[0];
                        if (outerRing) {
                        // 这里需要注意：如果 MultiPolygon 的每个子 Polygon 也有内环，
                        // 那么 outerRing 实际上是该子 Polygon 的所有环的数组。
                        // Google Maps Polygon 的 paths 属性可以接受 LatLngLiteral[][] 来表示外环和内环。
                        // 所以这里应该遍历 polygonPart 的所有环。
                            polygonPart.forEach(ring => {
                                pathsList.push(ring.map(([lng, lat]) => ({ lat, lng })));
                            });
                        }
                    }
                }
                break;
            default:
                break;
        }
        return pathsList;
    }, [searchType]);

    // --- 辅助函数：绘制 Feature 到地图上 (但初始设置为不可见) ---
    const drawFeatureOnMap = useCallback((
        feature: IFeaturesModel,
        dynamicColors: { fillColor: string; strokeColor: string },
        initialMap: google.maps.Map | null // 传入 null 表示初始不可见
    ): google.maps.Polygon[] => {
        // 不需要检查 map 是否存在，因为传入 initialMap 决定可见性
        if (!feature.geometry) return [];

        const polygonsForFeature: google.maps.Polygon[] = [];
        const pathsList = getGooglePolygonPaths(feature.geometry);

        if (pathsList.length > 0) {
            const polygon = new google.maps.Polygon({
                paths: pathsList,
                strokeColor: dynamicColors.strokeColor,
                // strokeOpacity: 0.6,
                strokeOpacity: 1,
                // strokeOpacity: feature.properties?.strokeopacity || 0.8,
                strokeWeight: parseFloat(feature.properties?.strokewidth || "2"),
                fillColor: dynamicColors.fillColor,
                // fillOpacity: feature.properties?.fillopacity || 0.35,
                fillOpacity: 1,
                map: initialMap, // 初始设置为 null 或传入实际的 map 实例
                zIndex: 999
            });
            polygonsForFeature.push(polygon);
        }
        return polygonsForFeature;
    }, [getGooglePolygonPaths]);

    // --- 辅助函数：清除所有已绘制的搜索结果多边形 ---
    const clearAllSearchResults = useCallback(() => {
        Object.values(allRenderedPolygonsRef.current).forEach(polygons => {
            polygons.forEach(polygon => polygon.setMap(null));
        });
        allRenderedPolygonsRef.current = {};
        currentDisplayedKeyRef.current = null;
        console.log("SearchResultOverlay: 所有搜索结果多边形已清除。");
    }, []);

    const bindPolygonEvents = useCallback(() => {
        Object.entries(allRenderedPolygonsRef.current).forEach(([key, polygons]) => {
            polygons.forEach(polygon => {
                // 避免重复绑定
                google.maps.event.clearListeners(polygon, "mouseover");
                google.maps.event.clearListeners(polygon, "mouseout");

                polygon.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
                    if (!infoWindowRef.current) {
                        infoWindowRef.current = new google.maps.InfoWindow();
                    }

                    // 由于 polygon 没有 feature 数据，可以在 polygon 上挂载属性以供展示
                    const feature: IFeaturesModel | undefined = (polygon as any).__feature;
                    if (!feature) return;

                    const value = feature.properties?.title?.toString() || "";
                    let title = "";
                    if(searchType === LightningSearchTypeEnum.PROBABILITY){
                        const tmpValue = value.split("-");
                        if(tmpValue?.length === 2){
                            title = `${(Number(tmpValue[0]) * 100).toFixed(0)}-${(Number(tmpValue[1]) * 100).toFixed(0)} %`;
                        }
                        else{
                            title = `${value} %`;
                        }
                    }
                    else if(searchType === LightningSearchTypeEnum.FIRSTSTRIKE){
                        const tmpValue = value.split("-");
                        if(tmpValue?.length === 2){
                            title = `${(Number(tmpValue[0])).toFixed(0)}-${(Number(tmpValue[1])).toFixed(0)} (min)`;
                        }
                        else{
                            title = `${value} (min)`;
                        }
                    }
                    else{
                        title = value;
                    }
                    // const mins = feature.properties?.mins?.toString() || "";
                    const displayContent = `
                    <div class="custom-info-window">
                        ${title}
                    </div>
                    `;

                    infoWindowRef.current.setContent(displayContent);
                    infoWindowRef.current.setPosition(event.latLng!);
                    infoWindowRef.current.open(map);
                });

                polygon.addListener("mouseout", () => {
                    infoWindowRef.current?.close();
                });
            });
        });
    }, [map, searchType]);

    // --- 副作用 1: 处理 finalSearchResult 变化，预加载所有多边形 ---
    useEffect(() => {
        if (!map) {
            console.log("SearchResultOverlay: 地图实例未准备好。等待地图加载。");
            return;
        }

        console.log("--- useEffect Re-run ---");
        console.log("finalSearchResult type:", typeof finalSearchResult);
        console.log("finalSearchResult value:", finalSearchResult);
        if (finalSearchResult) { // 只有当它不是 null/undefined 才尝试访问 .size
            console.log("finalSearchResult.size:", finalSearchResult.size);
        }

        // 每次 finalSearchResult 变化时，先清除所有旧的多边形
        clearAllSearchResults();
        onAllPolygonsLoaded(false); // 开始加载，通知父组件

        if (!finalSearchResult || finalSearchResult.size === 0) {
            console.log("SearchResultOverlay: finalSearchResult 为空，没有多边形需要渲染。");
            onAllPolygonsLoaded(true); // 没有数据，直接标记为加载完成
            return;
        }

        const newAllPolygons: ResultPolygonMap = {};
        const promises: Promise<void>[] = [];

        let keysToProcess: string[] = [];

        // 根据 selectType 决定键的遍历顺序
        if (searchType && searchType !== LightningSearchTypeEnum.FIRSTSTRIKE) {
            // 如果 selectType 有值且不等于 'FirstStrike'，按 Map 的原始顺序获取所有键
            keysToProcess = Array.from(finalSearchResult.keys());
        } else if (searchType === LightningSearchTypeEnum.FIRSTSTRIKE) {
            // 如果 selectType 等于 'FirstStrike'，则倒序获取所有键
            keysToProcess = Array.from(finalSearchResult.keys()).reverse();
        } else {
            // 如果 selectType 为 null/undefined 或其他情况，默认按原始顺序
            keysToProcess = Array.from(finalSearchResult.keys());
        }

        // 遍历处理后的键数组
        keysToProcess.forEach(key => {
            const featuresToRender = finalSearchResult.get(key); // 从 Map 中获取对应的 features 数组

            if (!newAllPolygons[key]) {
                newAllPolygons[key] = [];
            }

            if (featuresToRender) { // 确保 featuresToRender 不是 undefined
                if(searchType === LightningSearchTypeEnum.PROBABILITY){
                    ProbilityArray?.forEach((item)=>{
                        const feature = featuresToRender.find(f => f?.properties?.title?.trim()?.toString() === item);
                        if (feature &&
                            feature.geometry &&
                            (feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon") &&
                            feature.geometry.coordinates
                        ) {
                            const tmpMap = null;
                            // 获取动态颜色，此时 feature.properties.title 是 value 的来源
                            const dynamicColors = getDynamicColors(searchType, feature?.properties?.title?.trim()?.toString() || null);
                            // 初始创建时，将多边形设置为可见 (setMap(map))
                            const polygons = drawFeatureOnMap(feature, dynamicColors, tmpMap);
                            if (polygons.length > 0) {
                                // ⬇ 挂上原始 feature，供 hover 使用
                                polygons.forEach(polygon => {
                                    (polygon as any).__feature = feature;
                                });
                                newAllPolygons[key].push(...polygons);
                            }
                        }
                    });
                }
                else{
                    featuresToRender.forEach(feature => {
                        if (
                            feature.geometry &&
                        (feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon") &&
                        feature.geometry.coordinates
                        ) {
                            const tmpMap = searchType === LightningSearchTypeEnum.FIRSTSTRIKE ? map : null; // 如果是 FirstStrike，初始设置为不可见
                            // 获取动态颜色，此时 feature.properties.title 是 value 的来源
                            const dynamicColors = getDynamicColors(searchType, feature?.properties?.title?.trim()?.toString() || null);
                            // 初始创建时，将多边形设置为可见 (setMap(map))
                            const polygons = drawFeatureOnMap(feature, dynamicColors, tmpMap);
                            if (polygons.length > 0) {
                                // ⬇ 挂上原始 feature，供 hover 使用
                                polygons.forEach(polygon => {
                                    (polygon as any).__feature = feature;
                                });
                                newAllPolygons[key].push(...polygons);
                            }
                        }
                    });
                }

            }
        });

        // 将新创建的所有多边形存储到 ref 中
        allRenderedPolygonsRef.current = newAllPolygons;
        console.log("SearchResultOverlay: 所有多边形已预加载到内存中 (初始不可见)。");

        // ✅ 所有 polygon 创建后再统一绑定事件
        bindPolygonEvents();

        // 此时所有的多边形已经创建并存储在 ref 中，通知父组件加载完成
        onAllPolygonsLoaded(true);

        // 清理函数：组件卸载时清除所有多边形
        return () => {
            console.log("SearchResultOverlay: 组件卸载，清除所有搜索结果多边形。");
            clearAllSearchResults();
            onAllPolygonsLoaded(false); // 组件卸载时，加载状态重置
        };
    }, [map, finalSearchResult, searchType, getDynamicColors, drawFeatureOnMap, clearAllSearchResults, onAllPolygonsLoaded]);
    // }, [map, finalSearchResult, searchType, getDynamicColors, drawFeatureOnMap, clearAllSearchResults,]);

    // --- 副作用 2: 根据 activeKey 切换多边形的可见性 ---
    useEffect(() => {
        if (!map) return; // 地图未准备好则不执行

        const currentActivePolygons = allRenderedPolygonsRef.current[activeKey];
        const previouslyDisplayedKey = currentDisplayedKeyRef.current;
        if(searchType && searchType === LightningSearchTypeEnum.FIRSTSTRIKE){
            // 特殊处理：如果 currentKeyIndex 为 -1，表示没有选中任何项，则隐藏所有已显示的多边形
            if (currentKeyIndex !== -1 && Number(currentKeyIndex) >= 0) {
                Object.values(allRenderedPolygonsRef.current).forEach(polygons => {
                    polygons.forEach(polygon => polygon.setMap(null));
                });
                console.log("SearchResultOverlay: currentKeyIndex 为 -1，所有多边形已隐藏。");
            }
        }
        else{
            // 1. 隐藏之前显示的 activeKey 对应的多边形 (如果存在且与当前 activeKey 不同)
            if (previouslyDisplayedKey && previouslyDisplayedKey !== activeKey) {
                const polygonsToHide = allRenderedPolygonsRef.current[previouslyDisplayedKey];
                if (polygonsToHide) {
                    polygonsToHide.forEach(polygon => polygon.setMap(null));
                    console.log(`SearchResultOverlay: 隐藏 ${previouslyDisplayedKey} 对应的多边形。`);
                }
            }

        }


        // 2. 显示当前 activeKey 对应的多边形
        if (currentActivePolygons && currentActivePolygons.length > 0) {
            currentActivePolygons.forEach(polygon => polygon.setMap(map));
            console.log(`SearchResultOverlay: 显示 ${activeKey} 对应的多边形。`);
        } else {
            console.log(`SearchResultOverlay: ${activeKey} 没有对应的多边形数据或已清除。`);
        }

        // 更新当前显示在地图上的 key
        currentDisplayedKeyRef.current = activeKey;

    }, [map, activeKey,currentKeyIndex,searchType]); // 依赖项：map 和 activeKey

    useTrackedEffect(
        (changes,previousDeps,currentDeps) => {
            console.log("Index of changed dependencies: ", changes);
            console.log("Index of changed dependencies: ", previousDeps);
            console.log("Index of changed dependencies: ", currentDeps);
        },
        [map, finalSearchResult,searchType,getDynamicColors,drawFeatureOnMap,clearAllSearchResults,onAllPolygonsLoaded],
        // [map, finalSearchResult,searchType,getDynamicColors,drawFeatureOnMap,clearAllSearchResults],
    );

    console.log("lanxue searchResultOverlay allRenderedPolygonsRef.current", allRenderedPolygonsRef.current);
    console.log("lanxue searchResultOverlay finalSearchResult", finalSearchResult);
    console.log("lanxue searchResultOverlay activeKey", activeKey);

    return null; // 此组件不渲染任何可见的 React 元素，只操作地图
};

export default React.memo(SearchResultOverlay);